import { File05, Mark, Plus } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageSectionLayout from 'src/components/PageSectionLayout';
import Header from 'src/components/PageSectionLayout/Header/Header';
import { PATH } from 'src/constants/path';
import { ROUTES } from 'src/constants/routes';
import { type RootState } from 'src/redux/store';
import { getTimeBasedGreeting } from 'src/utils/CommonFunctions';

import { addQuoteIsModalOpen, resetAddQuoteData } from '../MyQuote/AddQuote/AddQuote.slice';
import { setCreateQuoteData } from '../MyQuote/CreateQuote/CreateQuote.slice';
import AddQuoteBasicDetailsModal from '../MyQuote/Modals/AddQuoteBasicDetailsModal';
import SelectBillingAccount from '../MyQuote/Modals/SelectBillingAccount';
import SelectDivision from '../MyQuote/Modals/SelectDivision';

import AccountSetupStatus from './AccountSetupStatus';
import ActivityFeed from './ActivityFeed';
import AskDashboardComposer from './AskDashboardComposer';
import TopNavigationList from './Components/TopNavigationList';
import UpcomingPickupDelivery from './UpcomingPickupDelivery';

const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
moment.tz.setDefault(browserTimezone);

const Dashboard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    const isShowAddQuoteBasicDetailsModal = useSelector(addQuoteIsModalOpen);
    const [isShowSelectDivisionModal, setIsShowSelectDivisionModal] = useState(false);
    const [isShowSelectBillingAccountModal, setIsShowSelectBillingAccountModal] = useState(false);

    const onNewQuote = () => {
        dispatch(resetAddQuoteData({ clearAll: false, openModal: true }));
    };

    const handleCloseSelectDivisionModal = () => {
        setIsShowSelectDivisionModal(false);
    };

    const handleCloseSelectBillingAccountModal = () => {
        setIsShowSelectBillingAccountModal(false);
    };

    const handleDivisionSelected = (divisionId: number) => {
        // After division is selected, check if we need to show billing account modal
        const selectedDivision = currentUser?.divisions?.find((div: any) => div.id === divisionId);

        if (selectedDivision) {
            const billingAccounts = selectedDivision?.customerBillingAccount || [];

            if (billingAccounts.length > 1) {
                setIsShowSelectBillingAccountModal(true);
            } else if (billingAccounts.length === 1) {
                dispatch(
                    setCreateQuoteData({
                        data: { customerBillingAccountId: billingAccounts[0]?.id },
                    })
                );
                navigate(PATH.CREATE_QUOTE);
            } else {
                navigate(PATH.CREATE_QUOTE);
            }
        }
    };

    const dashboardCards = [
        {
            icon: <Plus className="w-5 h-5 text-textSecondary" />,
            title: t('Dashboard.Create_New_Quote'),
            description: t('Dashboard.Create_New_Quote_Desc'),
            onClick: onNewQuote,
        },
        {
            icon: <Mark className="w-5 h-5 text-textSecondary" />,
            title: t('Dashboard.Track_My_Shipments'),
            description: t('Dashboard.Track_My_Shipments_Desc'),
            onClick: () => navigate(ROUTES.TRACK),
        },
        {
            icon: <File05 className="w-5 h-5 text-textSecondary" />,
            title: t('Dashboard.My_Documents'),
            description: t('Dashboard.My_Documents_Desc'),
            onClick: () => navigate(ROUTES.DOCUMENTATION_HUB),
        },
    ];

    return (
        <>
            <PageSectionLayout
                sectionClassName="overflow-y-auto scrollbar-hide"
                headerClassName="stepper-container"
                contentClassName="stepper-container"
                header={
                    <Header
                        title={
                            <>
                                {getTimeBasedGreeting(t)} <span className="text-gray500">{currentUser?.firstName || t('Dashboard.User')} 👋</span>
                            </>
                        }
                        isShowNotificationBox
                    />
                }
            >
                <ul className="flex flex-wrap gap-4 mb-3">
                    {dashboardCards.map((card, index) => (
                        <TopNavigationList key={index} icon={card.icon} title={card.title} description={card.description} onClick={card.onClick} />
                    ))}
                </ul>

                <AskDashboardComposer />

                <AccountSetupStatus onCreateQuote={onNewQuote} />
                <UpcomingPickupDelivery />
                <ActivityFeed />
                {/* <LatestUpdates /> */}
            </PageSectionLayout>

            {isShowSelectDivisionModal && <SelectDivision handleClose={handleCloseSelectDivisionModal} onDivisionSelected={handleDivisionSelected} />}

            {isShowSelectBillingAccountModal && <SelectBillingAccount handleClose={handleCloseSelectBillingAccountModal} />}

            {isShowAddQuoteBasicDetailsModal && <AddQuoteBasicDetailsModal handleClose={() => dispatch(resetAddQuoteData({ clearAll: false, openModal: false }))} />}
        </>
    );
};

export default Dashboard;
